import FacebookPixel from "@components/FacebookPixel"
import "@helpers/confidence"
import initializeSherlockObserver from "@helpers/initializeSherlockObserver"
import { beforeSend, DENY_URLS, IGNORE_ERRORS } from "@helpers/sentryFilter"
import { chooseSnowplowStateStorage } from "@helpers/snowplowStorage"
import ErrorPage from "@pages/ErrorPage"
import LoadingPage from "@pages/LoadingPage"
import routes from "@pages/routes"
import * as Sentry from "@sentry/react"
import { newTracker } from "@snowplow/browser-tracker"
import { Suspense, useEffect } from "react"
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom"
import "./i18n"
import "./index.css"

if (import.meta.env.MODE !== "development") {
  Sentry.init({
    // NOTE: Noise filters (other scripts' errors, user outcomes the pages
    // explain, test traffic on staging): see sentryFilter.ts.
    beforeSend,
    denyUrls: DENY_URLS,
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: import.meta.env.MODE,
    ignoreErrors: IGNORE_ERRORS,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect: useEffect,
        useLocation,
        useNavigationType,
      }),
    ],
    tracesSampleRate: 0, // NOTE: Disable performance monitoring.
  })
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter)

const router = sentryCreateBrowserRouter(routes)

// Created here rather than from an effect. React flushes effects child-first,
// so a route that renders in the same commit as `App` fires its page view
// before `App`'s effect has run — and Snowplow drops calls made while no
// tracker is registered, taking the event with them. Routes whose loaders
// defer the first render happen to avoid that, which makes it a property of
// the route rather than of the tracking. Nothing here needs React, so it runs
// at import time and the ordering question disappears.
newTracker("sp1", import.meta.env.VITE_SNOWPLOW_TRACKER_URL as string, {
  appId: import.meta.env.VITE_SNOWPLOW_APP_ID as string,
  platform: "web",
  // NOTE: Snowplow's default logs a console error when storage is blocked.
  stateStorageStrategy: chooseSnowplowStateStorage(),
})

export default function App() {
  useEffect(() => {
    if (import.meta.env.MODE === "development") return

    const sherlockObserver = initializeSherlockObserver()

    sherlockObserver.observe({ entryTypes: ["resource"] })

    return () => {
      sherlockObserver.disconnect()
    }
  }, [])

  // NOTE: `useTranslation` suspends until the translations have loaded. With
  // no Suspense boundary, a synchronous re-render in that window (React's
  // retry after an error, an error boundary showing the error page) failed
  // with React #426 instead (Sentry PRODUCTION-S6 / -S7).
  return (
    <Suspense fallback={<LoadingPage />}>
      <Sentry.ErrorBoundary
        fallback={({ error }) => <ErrorPage error={error} report={false} />}
      >
        <FacebookPixel
          pixelId={import.meta.env.VITE_FACEBOOK_PIXEL_ID as string}
        />

        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </Suspense>
  )
}
